/**
 * CompareDeck Component
 *
 * Task 2-3: Side-by-Side comparison deck in CDV sidebar.
 * Shows up to 2 selected responses for comparison with model names + logos.
 * Desktop-only, hidden on mobile/tablet.
 */

import React from 'react';
import { logger } from '../../../lib/logging';
import { ModelLogo } from '../../../utils/logoMapping';
import type { Response } from '../../../lib/types/results';
import { SplitViewIcon } from '../../icons/SplitViewIcon';

export interface CompareDeckProps {
  /** Responses currently in the comparison deck (max 2) */
  responses: Response[];
  /** Callback to remove a response from the deck */
  onRemove?: (responseId: string) => void;
  /** Whether sidebar is collapsed (show icon-only mode) */
  collapsed?: boolean;
  /** Correlation ID for telemetry */
  correlationId?: string;
  /** Task 5: Callback to open side-by-side view */
  onOpenSxS?: () => void;
  /** True when side-by-side view is active */
  isSxSMode?: boolean;
  /** Callback to exit side-by-side view and clear deck */
  onExitSxS?: () => void;
}

export const CompareDeck: React.FC<CompareDeckProps> = ({
  responses,
  onRemove,
  collapsed = false,
  correlationId,
  onOpenSxS,
  isSxSMode = false,
  onExitSxS
}) => {
  const handleRemove = (responseId: string) => {
    if (!onRemove) return;

    logger.info('sxs_deck_remove_clicked', correlationId, {
      op: 'sxs_deck_remove',
      component: 'CompareDeck',
      correlationId,
      responseId,
      deckSizeBefore: responses.length,
      success: true
    });

    onRemove(responseId);
  };

  const handleOpenSxS = () => {
    if (responses.length !== 2) return;

    if (isSxSMode && onExitSxS) {
      // Already in SxS mode - exit and clear deck
      logger.info('sxs_exit_from_deck_clicked', correlationId, {
        op: 'sxs_exit_from_deck',
        component: 'CompareDeck',
        correlationId,
        response1Id: responses[0].id,
        response2Id: responses[1].id,
        success: true
      });
      onExitSxS();
    } else if (!isSxSMode && onOpenSxS) {
      // Not in SxS mode - open view
      logger.info('sxs_open_clicked', correlationId, {
        op: 'sxs_open',
        component: 'CompareDeck',
        correlationId,
        response1Id: responses[0].id,
        response2Id: responses[1].id,
        success: true
      });
      onOpenSxS();
    }
  };

  // Hidden on mobile (md: breakpoint = 768px)
  return (
    <div
      className="hidden md:block pt-3 sm:pt-4 px-3 sm:px-4 border-t border-slate-700/50"
      data-testid="compare-deck"
    >
      {!collapsed && (
        <>
          <h3 className="text-xs sm:text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2 sm:mb-3">
            SIDE BY SIDE VIEW
          </h3>

          {/* Empty state */}
          {responses.length === 0 && (
            <div className="text-xs text-slate-500 text-center py-3 px-2 bg-slate-800/30 rounded-lg border border-slate-700/30">
              Select up to 2 responses to view them side by side
            </div>
          )}

          {/* Task 3: Deck items with model names + logos */}
          {responses.length > 0 && (
            <div className="space-y-2">
              {responses.map((response) => {
                const displayName = response.model?.displayName || response.modelId;

                return (
                  <div
                    key={response.id}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-700/30 border border-slate-600/50 rounded-lg text-xs text-slate-300"
                  >
                    {/* Model logo */}
                    <ModelLogo
                      modelId={response.modelId}
                      modelName={response.model?.name || undefined}
                      size="sm"
                      className="flex-shrink-0"
                    />

                    {/* Model display name */}
                    <span className="flex-1 truncate font-medium">
                      {displayName}
                    </span>

                    {/* Remove button */}
                    {onRemove && (
                      <button
                        onClick={() => handleRemove(response.id)}
                        aria-label={`Remove ${displayName} from compare deck`}
                        className="p-1 rounded hover:bg-slate-600/50 text-slate-400 hover:text-white transition-colors flex-shrink-0"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Task 5: Open Side-by-Side button (enabled when deck === 2) */}
          {responses.length === 2 && (onOpenSxS || onExitSxS) && (
            <button
              onClick={handleOpenSxS}
              className="w-full mt-3 px-4 py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/50 hover:border-cyan-400/70 rounded-lg text-sm font-semibold text-cyan-300 hover:text-cyan-200 transition-all flex items-center justify-center gap-2"
              aria-label={isSxSMode ? "Return to comparison view" : "Open side-by-side comparison"}
            >
              <SplitViewIcon className="w-5 h-5" />
              {isSxSMode ? 'Return to Comparison View' : 'Open Side-by-Side'}
            </button>
          )}
        </>
      )}
    </div>
  );
};
